/**
 * Notification Container Component
 *
 * Displays toast-style notifications from the UIStore.
 * Positioned at top-right with auto-dismiss and manual close.
 */

import React from 'react';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { useUIStore } from '../store/uiStore';

const NotificationContainer: React.FC = () => {
  const notifications = useUIStore((state) => state.notifications);
  const removeNotification = useUIStore((state) => state.removeNotification);

  if (notifications.length === 0) {
    return null;
  }

  const icons = {
    success: CheckCircle,
    error: AlertCircle,
    warning: AlertTriangle,
    info: Info,
  };

  const bgColors = {
    success: 'bg-green-50 border-green-500',
    error: 'bg-red-50 border-red-500',
    warning: 'bg-yellow-50 border-yellow-500',
    info: 'bg-tenant-50 border-tenant-500',
  };

  const textColors = {
    success: 'text-green-900',
    error: 'text-red-900',
    warning: 'text-yellow-900',
    info: 'text-tenant-900',
  };

  const iconColors = {
    success: 'text-green-600',
    error: 'text-red-600',
    warning: 'text-yellow-600',
    info: 'text-tenant-600',
  };

  return (
    <div
      className="fixed top-20 right-4 z-50 space-y-3 max-w-md w-full"
      role="region"
      aria-live="polite"
      aria-label="Notifications"
    >
      {notifications.map((notification) => {
        const Icon = icons[notification.type];
        const bgColor = bgColors[notification.type];
        const textColor = textColors[notification.type];
        const iconColor = iconColors[notification.type];

        return (
          <div
            key={notification.id}
            className={`${bgColor} border-l-4 rounded-lg shadow-lg p-4 animate-slide-in-right`}
            role="alert"
          >
            <div className="flex items-start">
              <Icon
                className={`w-5 h-5 ${iconColor} mr-3 flex-shrink-0 mt-0.5`}
                aria-hidden="true"
              />

              <div className="flex-1 min-w-0">
                {notification.title && (
                  <h3 className={`font-semibold ${textColor} mb-1`}>
                    {notification.title}
                  </h3>
                )}
                <p className={`text-sm ${textColor}`}>
                  {notification.message}
                </p>
              </div>

              <button
                onClick={() => removeNotification(notification.id)}
                className={`ml-3 flex-shrink-0 p-1 rounded-md hover:bg-black hover:bg-opacity-10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-current transition-colors ${textColor}`}
                aria-label="Fermer la notification"
              >
                <X className="w-4 h-4" aria-hidden="true" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default NotificationContainer;
